/**
 * Runtime loading of the researcher-chosen theme font — self-hosted ONLY.
 *
 * GDPR/CNIL: fonts must never be fetched from an external host (no Google
 * Fonts CDN). The loadable families below come from @fontsource packages
 * (OFL-licensed, woff2 shipped inside node_modules), so Vite bundles them and
 * the browser only ever talks to our own origin.
 *
 * Each loader dynamically imports the family's 400/600/700 CSS (+ Nunito's 800,
 * its title weight — see HEADING_WEIGHT_BY_FONT in tokens.ts). Vite
 * code-splits those imports into lazy CSS+woff2 chunks, so a deployment
 * downloads a font only when a study's THEME actually selects it. Dynamic
 * import is cached by the module system — calling a loader twice is a no-op.
 *
 * Fonts that need no loader:
 *   - 'Ciutadella Rounded' — commercial license, Mental+ only; shipped as a
 *     static asset and linked per-project from index.html (branding/brands.cjs
 *     fontHeadHtml). See public/assets/fonts/README.md.
 *   - 'System' — the OS font stack (see fontStack() in tokens.ts), zero bytes.
 *
 * Adding a family: npm i @fontsource/<name>, add a loader here, and extend the
 * allowlists in kastafiorementalplus-api/src/helpers/themeConfig.ts and the
 * Diapason theme editor font list.
 */

type FontLoader = () => Promise<unknown>;

const FONT_LOADERS: Record<string, FontLoader> = {
  Inter: () =>
    Promise.all([
      import("@fontsource/inter/400.css"),
      import("@fontsource/inter/600.css"),
      import("@fontsource/inter/700.css"),
    ]),
  Nunito: () =>
    Promise.all([
      import("@fontsource/nunito/400.css"),
      import("@fontsource/nunito/600.css"),
      import("@fontsource/nunito/700.css"),
      import("@fontsource/nunito/800.css"),
    ]),
  Poppins: () =>
    Promise.all([
      import("@fontsource/poppins/400.css"),
      import("@fontsource/poppins/600.css"),
      import("@fontsource/poppins/700.css"),
    ]),
  Outfit: () =>
    Promise.all([
      import("@fontsource/outfit/400.css"),
      import("@fontsource/outfit/600.css"),
      import("@fontsource/outfit/700.css"),
    ]),
  Quicksand: () =>
    Promise.all([
      import("@fontsource/quicksand/400.css"),
      import("@fontsource/quicksand/600.css"),
      import("@fontsource/quicksand/700.css"),
    ]),
};

/** Families that are valid theme fonts but need no runtime download. */
const NO_LOAD_FONTS = new Set(["System", "Ciutadella Rounded"]);

const warnedUnknown = new Set<string>();

/**
 * Ensure the @font-face rules (and woff2 files) for the given theme font are
 * loaded. Resolves immediately for undefined / no-load / unknown families —
 * unknown names warn once and fall back to fontStack()'s CSS fallbacks.
 */
export async function loadThemeFont(font?: string): Promise<void> {
  if (!font || NO_LOAD_FONTS.has(font)) return;

  const loader = FONT_LOADERS[font];
  if (!loader) {
    if (!warnedUnknown.has(font)) {
      warnedUnknown.add(font);
      console.warn(
        `[theme] No self-hosted loader for theme font "${font}" — text renders with the CSS fallback stack. ` +
          "Known loadable fonts: " + Object.keys(FONT_LOADERS).join(", ")
      );
    }
    return;
  }

  await loader();
}
